import React, { useState, useEffect, useCallback } from 'react';
import discountService from '../../../services/discountService';
import { refundAuthorityProblem, refundFieldsForSave } from '../../../utils/refundAuthority';

export const DiscountAuthoritySection = ({
  employeeId,
  centres = [],
  roles = [],
  showToast = () => {},
  isReadOnly = false,
  onDataChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Global authority state
  const [globalAuth, setGlobalAuth] = useState({
    hasDiscountAuthority: false,
    approvalMode: 'SelfApproval',
    maxDiscountPerBillPercent: 0,
    maxDiscountPerBillAmount: 0,
    dailyDiscountLimit: 0,
    monthlyDiscountLimit: 0,
    yearlyDiscountLimit: 0,
    canRefund: false,
    maxRefundPerTransaction: 0,
    dailyRefundLimit: 0,
    canSelfApprove: true,
    selfApprovalMaxPercent: 0,
    selfApprovalMaxAmount: 0,
    makerCheckerRequired: false,
    validFrom: '',
    validTo: '',
    isActive: true,
  });

  // Centre specific overrides
  const [centreAuthorities, setCentreAuthorities] = useState([]);
  // Approval rule tiers
  const [approvalRules, setApprovalRules] = useState([]);

  // Notify parent on state change
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        globalAuth,
        centreAuthorities,
        approvalRules,
      });
    }
  }, [globalAuth, centreAuthorities, approvalRules, onDataChange]);

  // Modal for editing center authority
  const [centreModal, setCentreModal] = useState({ open: false, isEdit: false, data: null });

  // Load authority data
  const loadAuthority = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await discountService.getEmployeeAuthority(employeeId);
      if (res.success && res.data) {
        const payload = res.data;
        const globalRow = payload.authorities.find((a) => a.centreId === null);
        if (globalRow) {
          setGlobalAuth({
            ...globalRow,
            validFrom: globalRow.validFrom ? globalRow.validFrom.split('T')[0] : '',
            validTo: globalRow.validTo ? globalRow.validTo.split('T')[0] : '',
          });
        } else {
          setGlobalAuth((prev) => ({ ...prev, hasDiscountAuthority: false }));
        }

        const centreRows = payload.authorities.filter((a) => a.centreId !== null);
        setCentreAuthorities(
          centreRows.map((c) => ({
            ...c,
            validFrom: c.validFrom ? c.validFrom.split('T')[0] : '',
            validTo: c.validTo ? c.validTo.split('T')[0] : '',
          }))
        );

        setApprovalRules(payload.rules || []);
      }
    } catch (err) {
      console.error('Failed to load discount authority:', err);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    loadAuthority();
  }, [loadAuthority]);

  // Handle saving
  const handleSave = async () => {
    if (!employeeId) {
      showToast('Authority settings staged! Click "Save Employee" at the bottom to register staff and save these limits.', 'info');
      return;
    }
    const refundProblem = [globalAuth, ...centreAuthorities].map(refundAuthorityProblem).find(Boolean);
    if (refundProblem) {
      showToast(refundProblem, 'error');
      return;
    }
    setSaving(true);
    try {
      const allAuthorities = [
        {
          ...globalAuth,
          employeeId,
          centreId: null,
          maxDiscountPerBillPercent: parseFloat(globalAuth.maxDiscountPerBillPercent) || 0,
          maxDiscountPerBillAmount: parseFloat(globalAuth.maxDiscountPerBillAmount) || 0,
          dailyDiscountLimit: parseFloat(globalAuth.dailyDiscountLimit) || 0,
          monthlyDiscountLimit: parseFloat(globalAuth.monthlyDiscountLimit) || 0,
          yearlyDiscountLimit: parseFloat(globalAuth.yearlyDiscountLimit) || 0,
          ...refundFieldsForSave(globalAuth),
          selfApprovalMaxPercent: parseFloat(globalAuth.selfApprovalMaxPercent) || 0,
          selfApprovalMaxAmount: parseFloat(globalAuth.selfApprovalMaxAmount) || 0,
          validFrom: globalAuth.validFrom || null,
          validTo: globalAuth.validTo || null,
        },
        ...centreAuthorities.map((c) => ({
          ...c,
          employeeId,
          maxDiscountPerBillPercent: parseFloat(c.maxDiscountPerBillPercent) || 0,
          maxDiscountPerBillAmount: parseFloat(c.maxDiscountPerBillAmount) || 0,
          dailyDiscountLimit: parseFloat(c.dailyDiscountLimit) || 0,
          monthlyDiscountLimit: parseFloat(c.monthlyDiscountLimit) || 0,
          yearlyDiscountLimit: parseFloat(c.yearlyDiscountLimit) || 0,
          ...refundFieldsForSave(c),
          selfApprovalMaxPercent: parseFloat(c.selfApprovalMaxPercent) || 0,
          selfApprovalMaxAmount: parseFloat(c.selfApprovalMaxAmount) || 0,
          validFrom: c.validFrom || null,
          validTo: c.validTo || null,
        })),
      ];

      const cleanedRules = approvalRules.map((r, idx) => ({
        ...r,
        employeeId,
        levelNumber: idx + 1,
        minAmount: parseFloat(r.minAmount) || 0,
        maxAmount: parseFloat(r.maxAmount) || 99999999.99,
        approverRoleId: r.approverRoleId ? parseInt(r.approverRoleId, 10) : null,
        approverEmployeeId: r.approverEmployeeId ? parseInt(r.approverEmployeeId, 10) : null,
      }));

      const payload = {
        employeeId,
        authorities: allAuthorities,
        rules: cleanedRules,
      };

      const res = await discountService.saveEmployeeAuthority(employeeId, payload);
      if (res.success) {
        showToast('Discount & approval authority successfully saved!', 'success');
        loadAuthority();
      }
    } catch (err) {
      console.error('Error saving discount authority:', err);
      showToast(err.response?.data?.message || 'Failed to save discount authority.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Add rule tier
  const handleAddTier = () => {
    const nextLevel = approvalRules.length + 1;
    const lastRule = approvalRules[approvalRules.length - 1];
    const newMin = lastRule ? parseFloat(lastRule.maxAmount) + 1 : 0;
    setApprovalRules([
      ...approvalRules,
      {
        levelNumber: nextLevel,
        minAmount: newMin,
        maxAmount: newMin + 5000,
        approverType: 'Role',
        approverRoleId: roles[0]?.id || 1,
        isActive: true,
      },
    ]);
  };

  const handleRemoveTier = (idx) => {
    setApprovalRules(approvalRules.filter((_, i) => i !== idx));
  };

  // Save centre override modal
  const handleSaveCentreModal = (e) => {
    e.preventDefault();
    const data = centreModal.data;
    if (!data.centreId) {
      showToast('Please select a diagnostic center.', 'error');
      return;
    }
    const targetCentre = centres.find((c) => String(c.id) === String(data.centreId));
    const formatted = {
      ...data,
      centreName: targetCentre?.name || 'Center',
      centreCode: targetCentre?.code || '',
    };

    if (centreModal.isEdit) {
      setCentreAuthorities(
        centreAuthorities.map((item) => (item.centreId === data.centreId ? formatted : item))
      );
    } else {
      setCentreAuthorities([
        ...centreAuthorities.filter((item) => item.centreId !== data.centreId),
        formatted,
      ]);
    }
    setCentreModal({ open: false, isEdit: false, data: null });
  };

  return (
    <div className="discount-authority-container" style={{ marginTop: '1.25rem' }}>
      {loading ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          Loading discount authority settings...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* 1. Global Authority Switch & Mode */}
          <div
            style={{
              padding: '1.25rem',
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              border: '1px solid var(--border-color)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  🎟️ Master Discount Granting Authority
                </h4>
                <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  Enables this staff member to apply concessions during patient registration and billing.
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={globalAuth.hasDiscountAuthority}
                    onChange={(e) => setGlobalAuth({ ...globalAuth, hasDiscountAuthority: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    disabled={isReadOnly}
                  />
                  <span>Authority Enabled</span>
                </label>
              </div>
            </div>

            {globalAuth.hasDiscountAuthority && (
              <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--border-color)' }}>
                {/* Approval Mode Selection */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    Approval Mode
                  </label>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {[
                      { id: 'SelfApproval', label: 'Self Approval (Full autonomy within limits)', desc: 'Small lab / trusted staff' },
                      { id: 'ApprovalRequired', label: 'Approval Required (All concessions queued)', desc: 'Enterprise maker-checker' },
                      { id: 'ThresholdBased', label: 'Threshold Based (Self-approve small, escalate large)', desc: 'Tiered escalation' },
                    ].map((mode) => (
                      <button
                        key={mode.id}
                        type="button"
                        onClick={() => setGlobalAuth({ ...globalAuth, approvalMode: mode.id })}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '8px',
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          border: globalAuth.approvalMode === mode.id ? '2px solid var(--accent)' : '1px solid var(--border-color)',
                          background: globalAuth.approvalMode === mode.id ? 'rgba(66, 135, 245, 0.1)' : 'var(--bg-card)',
                          color: globalAuth.approvalMode === mode.id ? 'var(--accent)' : 'var(--text-primary)',
                          textAlign: 'left',
                        }}
                      >
                        <div>{mode.label}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>{mode.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Per-Bill Limits Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Max Discount % Per Bill
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      className="form-input"
                      value={globalAuth.maxDiscountPerBillPercent}
                      onChange={(e) => setGlobalAuth({ ...globalAuth, maxDiscountPerBillPercent: e.target.value })}
                      placeholder="e.g. 15%"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Max Discount Amount Per Bill (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="50"
                      className="form-input"
                      value={globalAuth.maxDiscountPerBillAmount}
                      onChange={(e) => setGlobalAuth({ ...globalAuth, maxDiscountPerBillAmount: e.target.value })}
                      placeholder="e.g. 1500"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Daily Limit (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="500"
                      className="form-input"
                      value={globalAuth.dailyDiscountLimit}
                      onChange={(e) => setGlobalAuth({ ...globalAuth, dailyDiscountLimit: e.target.value })}
                      placeholder="e.g. 10000"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Monthly Limit (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      className="form-input"
                      value={globalAuth.monthlyDiscountLimit}
                      onChange={(e) => setGlobalAuth({ ...globalAuth, monthlyDiscountLimit: e.target.value })}
                      placeholder="e.g. 50000"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Self-Approval Max Amount (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="50"
                      className="form-input"
                      value={globalAuth.selfApprovalMaxAmount}
                      onChange={(e) => setGlobalAuth({ ...globalAuth, selfApprovalMaxAmount: e.target.value })}
                      placeholder="e.g. 500"
                      disabled={globalAuth.approvalMode === 'ApprovalRequired'}
                    />
                  </div>
                </div>

                {/* Refund authority */}
                <div style={{ marginTop: '1.25rem', padding: '0.9rem 1rem', border: '1px solid var(--border-color)', borderRadius: '10px', background: 'var(--bg-secondary)' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!globalAuth.canRefund}
                      onChange={(e) => setGlobalAuth({ ...globalAuth, canRefund: e.target.checked })}
                    />
                    <span>Can give refunds</span>
                  </label>
                  <span style={{ marginLeft: '10px', fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                    A refund above these limits is refused for this person, whatever their role.
                  </span>
                  {globalAuth.canRefund && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px', marginTop: '10px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                          Max Refund Per Refund (₹)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="100"
                          className="form-input"
                          value={globalAuth.maxRefundPerTransaction ?? 0}
                          onChange={(e) => setGlobalAuth({ ...globalAuth, maxRefundPerTransaction: e.target.value })}
                          placeholder="e.g. 500"
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                          Daily Refund Limit (₹, 0 = no daily cap)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="500"
                          className="form-input"
                          value={globalAuth.dailyRefundLimit ?? 0}
                          onChange={(e) => setGlobalAuth({ ...globalAuth, dailyRefundLimit: e.target.value })}
                          placeholder="e.g. 5000"
                        />
                      </div>
                    </div>
                  )}
                  {refundAuthorityProblem(globalAuth) && (
                    <div role="alert" style={{ marginTop: '8px', fontSize: '0.78rem', color: 'var(--danger, #dc2626)', fontWeight: 600 }}>
                      {refundAuthorityProblem(globalAuth)}
                    </div>
                  )}
                </div>

                {/* Maker-Checker & Validity Row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap', marginTop: '1.25rem' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={globalAuth.makerCheckerRequired}
                      onChange={(e) => setGlobalAuth({ ...globalAuth, makerCheckerRequired: e.target.checked })}
                    />
                    <span>Strict Maker-Checker (Requester cannot approve own request)</span>
                  </label>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Authority Validity:</span>
                    <input
                      type="date"
                      className="form-input"
                      style={{ padding: '4px 8px', fontSize: '0.8rem', width: '135px' }}
                      value={globalAuth.validFrom}
                      onChange={(e) => setGlobalAuth({ ...globalAuth, validFrom: e.target.value })}
                      title="Valid From"
                    />
                    <span>to</span>
                    <input
                      type="date"
                      className="form-input"
                      style={{ padding: '4px 8px', fontSize: '0.8rem', width: '135px' }}
                      value={globalAuth.validTo}
                      onChange={(e) => setGlobalAuth({ ...globalAuth, validTo: e.target.value })}
                      title="Valid To (Expiry)"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 2. Threshold Approval Tiers (Visible when Threshold Based) */}
          {globalAuth.hasDiscountAuthority && globalAuth.approvalMode === 'ThresholdBased' && (
            <div
              style={{
                padding: '1.25rem',
                background: 'var(--bg-secondary)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div>
                  <h4 style={{ margin: '0 0 2px 0', fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    📊 Multi-Tier Approval Rules
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    Define escalating approver tiers based on requested concession amounts.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleAddTier}
                  style={{ fontSize: '0.78rem', padding: '5px 12px' }}
                >
                  + Add Approval Tier
                </button>
              </div>

              {approvalRules.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', background: 'var(--bg-card)', borderRadius: '8px', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                  No custom tiers configured. Defaulting to Level 1 Center Head / Administrator.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {approvalRules.map((rule, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        background: 'var(--bg-card)',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--accent)', minWidth: '60px' }}>
                        Level {idx + 1}
                      </span>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>From ₹</span>
                        <input
                          type="number"
                          style={{ width: '90px', padding: '4px 8px', fontSize: '0.82rem' }}
                          className="form-input"
                          value={rule.minAmount}
                          onChange={(e) => {
                            const updated = [...approvalRules];
                            updated[idx].minAmount = e.target.value;
                            setApprovalRules(updated);
                          }}
                        />
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>to ₹</span>
                        <input
                          type="number"
                          style={{ width: '90px', padding: '4px 8px', fontSize: '0.82rem' }}
                          className="form-input"
                          value={rule.maxAmount}
                          onChange={(e) => {
                            const updated = [...approvalRules];
                            updated[idx].maxAmount = e.target.value;
                            setApprovalRules(updated);
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Approver:</span>
                        <select
                          className="form-select"
                          style={{ padding: '4px 8px', fontSize: '0.82rem', minWidth: '160px' }}
                          value={rule.approverRoleId || ''}
                          onChange={(e) => {
                            const updated = [...approvalRules];
                            updated[idx].approverRoleId = e.target.value;
                            setApprovalRules(updated);
                          }}
                        >
                          <option value="">-- Select Approver Role --</option>
                          {roles.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveTier(idx)}
                        style={{
                          marginLeft: 'auto',
                          background: 'none',
                          border: 'none',
                          color: 'var(--danger-color)',
                          cursor: 'pointer',
                          fontSize: '1rem',
                        }}
                        title="Remove Tier"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 3. Center-Wise Authority Overrides */}
          {globalAuth.hasDiscountAuthority && (
            <div
              style={{
                padding: '1.25rem',
                background: 'var(--bg-secondary)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div>
                  <h4 style={{ margin: '0 0 2px 0', fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    🏥 Center-Specific Discount Authority
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    Override default limits for specific diagnostic facilities or satellite collection centers.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    setCentreModal({
                      open: true,
                      isEdit: false,
                      data: {
                        centreId: centres[0]?.id || 1,
                        hasDiscountAuthority: true,
                        approvalMode: 'SelfApproval',
                        maxDiscountPerBillPercent: 10,
                        maxDiscountPerBillAmount: 500,
                        dailyDiscountLimit: 5000,
                        monthlyDiscountLimit: 25000,
                        canSelfApprove: true,
                        selfApprovalMaxAmount: 500,
                        canRefund: false,
                        maxRefundPerTransaction: 0,
                        dailyRefundLimit: 0,
                        isActive: true,
                      },
                    })
                  }
                  style={{ fontSize: '0.78rem', padding: '5px 12px' }}
                >
                  + Add Center Override
                </button>
              </div>

              {centreAuthorities.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', background: 'var(--bg-card)', borderRadius: '8px', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                  No center-specific overrides. Default global authority applies across all assigned centers.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-card)', textAlign: 'left', borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ padding: '8px 10px' }}>Diagnostic Center</th>
                        <th style={{ padding: '8px 10px' }}>Mode</th>
                        <th style={{ padding: '8px 10px' }}>Max %</th>
                        <th style={{ padding: '8px 10px' }}>Max ₹</th>
                        <th style={{ padding: '8px 10px' }}>Daily Limit</th>
                        <th style={{ padding: '8px 10px' }}>Status</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {centreAuthorities.map((ca, idx) => (
                        <tr key={ca.centreId || idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '8px 10px', fontWeight: 600 }}>{ca.centreName}</td>
                          <td style={{ padding: '8px 10px' }}>{ca.approvalMode}</td>
                          <td style={{ padding: '8px 10px' }}>{ca.maxDiscountPerBillPercent}%</td>
                          <td style={{ padding: '8px 10px' }}>₹{ca.maxDiscountPerBillAmount}</td>
                          <td style={{ padding: '8px 10px' }}>₹{ca.dailyDiscountLimit}</td>
                          <td style={{ padding: '8px 10px' }}>
                            <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 600, background: ca.isActive ? '#dcfce7' : '#fee2e2', color: ca.isActive ? '#15803d' : '#b91c1c' }}>
                              {ca.isActive ? 'Active' : 'Disabled'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                            <button
                              type="button"
                              onClick={() => setCentreModal({ open: true, isEdit: true, data: { ...ca } })}
                              style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', marginRight: '8px' }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => setCentreAuthorities(centreAuthorities.filter((item) => item.centreId !== ca.centreId))}
                              style={{ background: 'none', border: 'none', color: 'var(--danger-color)', cursor: 'pointer' }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Action Bar */}
          {!isReadOnly && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
                style={{ minWidth: '160px' }}
              >
                {saving ? 'Saving Authority...' : 'Save Discount Authority'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Centre Authority Modal */}
      {centreModal.open && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="glass-card" style={{ width: '480px', maxWidth: '95vw', background: 'var(--bg-primary)', padding: '1.5rem', borderRadius: '12px' }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.05rem', fontWeight: 700 }}>
              {centreModal.isEdit ? 'Edit Center Discount Authority' : 'Add Center Authority Override'}
            </h3>
            <form onSubmit={handleSaveCentreModal}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label className="form-label">Diagnostic Center</label>
                  <select
                    className="form-select"
                    value={centreModal.data.centreId}
                    onChange={(e) => setCentreModal({ ...centreModal, data: { ...centreModal.data, centreId: parseInt(e.target.value, 10) } })}
                    disabled={centreModal.isEdit}
                  >
                    {centres.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label className="form-label">Max Discount %</label>
                    <input
                      type="number"
                      className="form-input"
                      value={centreModal.data.maxDiscountPerBillPercent}
                      onChange={(e) => setCentreModal({ ...centreModal, data: { ...centreModal.data, maxDiscountPerBillPercent: e.target.value } })}
                    />
                  </div>
                  <div>
                    <label className="form-label">Max Amount (₹)</label>
                    <input
                      type="number"
                      className="form-input"
                      value={centreModal.data.maxDiscountPerBillAmount}
                      onChange={(e) => setCentreModal({ ...centreModal, data: { ...centreModal.data, maxDiscountPerBillAmount: e.target.value } })}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label className="form-label">Daily Limit (₹)</label>
                    <input
                      type="number"
                      className="form-input"
                      value={centreModal.data.dailyDiscountLimit}
                      onChange={(e) => setCentreModal({ ...centreModal, data: { ...centreModal.data, dailyDiscountLimit: e.target.value } })}
                    />
                  </div>
                  <div>
                    <label className="form-label">Monthly Limit (₹)</label>
                    <input
                      type="number"
                      className="form-input"
                      value={centreModal.data.monthlyDiscountLimit}
                      onChange={(e) => setCentreModal({ ...centreModal, data: { ...centreModal.data, monthlyDiscountLimit: e.target.value } })}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '6px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!centreModal.data.canRefund}
                      onChange={(e) => setCentreModal({ ...centreModal, data: { ...centreModal.data, canRefund: e.target.checked } })}
                    />
                    <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Can give refunds at this Center</span>
                  </label>
                  {centreModal.data.canRefund && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '8px' }}>
                      <div>
                        <label className="form-label">Max Per Refund (₹)</label>
                        <input
                          type="number"
                          min="0"
                          className="form-input"
                          value={centreModal.data.maxRefundPerTransaction ?? 0}
                          onChange={(e) => setCentreModal({ ...centreModal, data: { ...centreModal.data, maxRefundPerTransaction: e.target.value } })}
                        />
                      </div>
                      <div>
                        <label className="form-label">Daily Refund Limit (₹, 0 = no cap)</label>
                        <input
                          type="number"
                          min="0"
                          className="form-input"
                          value={centreModal.data.dailyRefundLimit ?? 0}
                          onChange={(e) => setCentreModal({ ...centreModal, data: { ...centreModal.data, dailyRefundLimit: e.target.value } })}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '6px' }}>
                  <input
                    type="checkbox"
                    checked={centreModal.data.canSelfApprove}
                    onChange={(e) => setCentreModal({ ...centreModal, data: { ...centreModal.data, canSelfApprove: e.target.checked } })}
                  />
                  <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Enable Self-Approval for this Center</span>
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCentreModal({ open: false, isEdit: false, data: null })}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Override
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountAuthoritySection;
